import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment/moment'

import {
    apiChangePassword,
    apiForgotPassword,
    apiLogIn,
    apiLogOut,
    apiProfile,
    apiRefreshToken,
    apiResend,
    apiResetPassword,
    apiSignature,
    apiUser,
    apiVerifyEmail,
} from 'Services/AuthService'
import { setUser, userLoggedOut } from 'Store/auth/userSlice'
import { onSignInSuccess, onSignOutSuccess, setToken } from 'Store/auth/sessionSlice'
import { Success } from 'Utils/alerts'

function useAuth() {
    const [loading, setLoading] = useState(true)
    const [loginError, setLoginError] = useState(null)
    const location = useLocation()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { session, user } = useSelector(state => state.auth)

    const clearLoginError = () => setLoginError(null)

    const signIn = (values, { setSubmitting }) => {
        setSubmitting(true)
        setLoginError(null)
        apiLogIn(values)
            .then(({ data }) => {
                dispatch(onSignInSuccess(data))
                dispatch(setUser(data.user))
                Success(data.message)
                if (location?.state?.from) {
                    navigate(location.state.from)
                } else {
                    navigate(`/${data.user.guard}`)
                }
            })
            .catch(err => {
                // Prefer the server-supplied message when we got a real
                // response. Fall back to a plain-English message for the
                // no-response network-error case so the user doesn't just
                // see "Network Error".
                const networkErrorMessage =
                    err?.message === 'Network Error' || err?.code === 'ERR_NETWORK' || err?.code === 'ECONNABORTED'
                        ? 'Could not reach the server. Check your connection and try again.'
                        : null
                if (networkErrorMessage) {
                    setLoginError(networkErrorMessage)
                }
            })
            .finally(() => setSubmitting(false))
    }

    const forgotPassword = (values, { setSubmitting }) => {
        setSubmitting(true)
        apiForgotPassword(values)
            .then(({ data }) => {
                Success(data.message)
                navigate('/login')
            })
            .catch(error => error)
            .finally(() => setSubmitting(false))
    }

    const resetPassword = (values, { setSubmitting }) => {
        setSubmitting(true)
        apiResetPassword(values)
            .then(({ data }) => {
                Success(data.message)
                navigate('/login')
            })
            .catch(error => error)
            .finally(() => setSubmitting(false))
    }

    const handleSignOut = () => {
        dispatch(onSignOutSuccess())
        dispatch(userLoggedOut())
        navigate('/login')
    }

    const signOut = () => {
        apiLogOut(user.guard)
            .then(({ data }) => Success(data.message))
            .catch(error => error)
            .finally(handleSignOut)
    }

    const getUser = guard => {
        apiUser(guard)
            .then(({ data }) => dispatch(setUser(data)))
            .catch(handleSignOut)
            .finally(() => setTimeout(() => setLoading(false), 100))
    }

    const resendEmail = setSubmitting => {
        setSubmitting(true)
        apiResend(user.guard)
            .then(({ data }) => Success(data.message))
            .catch(error => error)
            .finally(() => setSubmitting(false))
    }

    const verifyEmail = (id, hash, expires, signature) => {
        if (!user?.guard) return
        apiVerifyEmail(user.guard, id, hash, expires, signature)
            .then(({ data }) => {
                Success(data.message)
                getUser(user.guard)
            })
            .catch(error => error)
    }

    const checkToken = (guard, location) => {
        const now = moment().add(5, 'minute').format('YYYY-MM-DD HH:mm:ss')
        const tokenExpires = moment(session.token_expires_at).format('YYYY-MM-DD HH:mm:ss')
        const refreshTokenExpires = moment(session.refresh_token_expires_at).format('YYYY-MM-DD HH:mm:ss')
        if (session.refresh_token_expires_at && now > refreshTokenExpires) {
            signOut()
        } else if (now > tokenExpires) {
            apiRefreshToken()
                .then(({ data }) => dispatch(setToken(data)))
                .catch(handleSignOut)
                .finally(() => setTimeout(() => setLoading(false), 100))
        } else if (user?.now) {
            const verifyEmailUrl = `/${user.guard}/verify-email`
            if (now > user.now) {
                getUser(guard)
            } else if (!user.email_verified_at) {
                if (!location.includes(verifyEmailUrl)) {
                    navigate(verifyEmailUrl)
                } else {
                    setLoading(false)
                }
            } else if (location.includes(verifyEmailUrl)) {
                navigate(`/${user.guard}`)
            } else {
                setLoading(false)
            }
        }
    }

    const loginCheck = (authenticated, guard, location) => {
        if (guard === 'guest') {
            if (authenticated) {
                navigate(`/${user.guard}`)
            } else {
                setLoading(false)
            }
        } else if (authenticated) {
            if (guard !== user.guard) {
                navigate(`/${user.guard}`)
            } else {
                checkToken(user.guard, location)
            }
        } else {
            navigate('/login', { state: { from: location } })
        }
    }

    const changePassword = (values, { setSubmitting, resetForm }) => {
        setSubmitting(true)
        apiChangePassword(user.guard, values)
            .then(({ data }) => {
                Success(data.message)
                resetForm()
            })
            .catch(error => error)
            .finally(() => setSubmitting(false))
    }

    const profile = (values, { setSubmitting }) => {
        setSubmitting(true)
        apiProfile(user.guard, values)
            .then(({ data }) => {
                getUser(user.guard)
                Success(data.message)
            })
            .catch(error => error)
            .finally(() => setSubmitting(false))
    }

    const signature = (values, { setSubmitting }) => {
        setSubmitting(true)
        apiSignature(user.guard, values)
            .then(({ data }) => {
                getUser(user.guard)
                Success(data.message)
                navigate(`/${user.guard}`)
            })
            .catch(error => error)
            .finally(() => setSubmitting(false))
    }

    return {
        loading,
        authenticated: !!(session.token && session.signedIn && user?.guard),
        signIn,
        loginError,
        clearLoginError,
        forgotPassword,
        resetPassword,
        signOut,
        handleSignOut,
        resendEmail,
        verifyEmail,
        loginCheck,
        changePassword,
        profile,
        signature,
    }
}

export default useAuth
